import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart,
  faHandshake,
  faRing,
  faHome,
  faUsers,
  faChild,
  faSpinner,
  faStar,
  faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

const RelationshipNumerologyPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [formData, setFormData] = useState({
    person1Name: '',
    person1BirthDate: '',
    person2Name: '',
    person2BirthDate: '',
    focusArea: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user is logged in
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    
    if (!formData.person1Name || !formData.person1BirthDate || !formData.person2Name || !formData.person2BirthDate) {
      alert(t('pleaseCompleteRequiredFields'));
      return;
    }

    setIsAnalyzing(true);
    
    try {
      // API call will be implemented here
      await new Promise(resolve => setTimeout(resolve, 3000));
      console.log('Relationship analysis data:', formData);
    } catch (error) {
      console.error('Analysis failed:', error);
      alert(t('analysisError'));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    padding: '40px 20px',
    background: 'linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)'
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '3rem'
  };

  const analysisCards = [
    {
      title: t('romanticCompatibility'),
      description: t('romanticCompatibilityDesc'),
      icon: faHeart,
      color: 'linear-gradient(135deg, #ec4899, #be185d)',
      bgColor: '#fce7f3'
    },
    {
      title: t('friendshipCompatibility'),
      description: t('friendshipCompatibilityDesc'),
      icon: faHandshake,
      color: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      bgColor: '#dbeafe'
    },
    {
      title: t('marriageNumerology'),
      description: t('marriageNumerologyDesc'),
      icon: faRing,
      color: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
      bgColor: '#e9d5ff'
    },
    {
      title: t('familyDynamics'),
      description: t('familyDynamicsDesc'),
      icon: faHome,
      color: 'linear-gradient(135deg, #10b981, #059669)',
      bgColor: '#d1fae5'
    },
    {
      title: t('businessPartnership'),
      description: t('businessPartnershipDesc'),
      icon: faUsers,
      color: 'linear-gradient(135deg, #f59e0b, #d97706)',
      bgColor: '#fef3c7'
    },
    {
      title: t('parentChildRelationship'),
      description: t('parentChildRelationshipDesc'),
      icon: faChild,
      color: 'linear-gradient(135deg, #06b6d4, #0891b2)',
      bgColor: '#cffafe'
    }
  ];

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '1.5rem',
    marginBottom: '4rem'
  };

  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '16px',
    padding: '1.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  };

  const formContainerStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '20px',
    padding: '2.5rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(236, 72, 153, 0.1)',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem'
  };

  const formRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem'
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '0.75rem',
    display: 'block',
    letterSpacing: '0.025em'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px 20px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    background: '#fafafa',
    fontSize: '1.1rem',
    transition: 'all 0.3s ease',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: '#374151',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '18px 32px',
    background: 'linear-gradient(135deg, #ec4899, #be185d)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1.1rem',
    fontWeight: '700',
    cursor: isAnalyzing ? 'not-allowed' : 'pointer',
    opacity: isAnalyzing ? 0.7 : 1,
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    marginTop: '1rem',
    boxShadow: '0 4px 14px 0 rgba(236, 72, 153, 0.3)',
    letterSpacing: '0.025em'
  };

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #ec4899, #be185d)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '1rem',
            letterSpacing: '-0.025em'
          }}>
            {t('relationshipNumerologyTitle')}
          </h1>
          <p style={{
            fontSize: '1.25rem',
            color: '#6b7280',
            maxWidth: '800px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            {t('relationshipNumerologySubtitle')}
          </p>
        </div>

        {/* Analysis Cards */}
        <div style={gridStyle}>
          {analysisCards.map((card, index) => (
            <div
              key={index}
              style={{
                ...cardStyle,
                background: `linear-gradient(135deg, ${card.bgColor} 0%, white 100%)`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: card.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '1rem'
                }}>
                  <FontAwesomeIcon icon={card.icon} style={{ color: 'white', fontSize: '1.25rem' }} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', margin: 0 }}>
                  {card.title}
                </h3>
              </div>
              <p style={{ color: '#6b7280', lineHeight: 1.6, margin: 0, fontSize: '0.95rem' }}>
                {card.description}
              </p>
            </div>
          ))}
        </div>

        {/* Analysis Form */}
        <div style={formContainerStyle}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            {t('relationshipAnalysisTitle')}
          </h2>
          <p style={{
            color: '#6b7280',
            textAlign: 'center',
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: '1.6'
          }}>
            {t('relationshipAnalysisSubtitle')}
          </p>

          <form onSubmit={handleAnalyze} style={formStyle}>
            <div style={formRowStyle}>
              <div>
                <label style={labelStyle}>{t('person1Name')}</label>
                <input
                  type="text"
                  name="person1Name"
                  value={formData.person1Name}
                  onChange={handleInputChange}
                  placeholder={t('namePlaceholder')}
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ec4899';
                    e.target.style.background = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.background = '#fafafa';
                  }}
                />
              </div>

              <div>
                <label style={labelStyle}>{t('person1BirthDate')}</label>
                <input
                  type="date"
                  name="person1BirthDate"
                  value={formData.person1BirthDate}
                  onChange={handleInputChange}
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ec4899';
                    e.target.style.background = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.background = '#fafafa';
                  }}
                />
              </div>
            </div>

            <div style={formRowStyle}>
              <div>
                <label style={labelStyle}>{t('person2Name')}</label>
                <input
                  type="text"
                  name="person2Name"
                  value={formData.person2Name}
                  onChange={handleInputChange}
                  placeholder={t('namePlaceholder')}
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ec4899';
                    e.target.style.background = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.background = '#fafafa';
                  }}
                />
              </div>

              <div>
                <label style={labelStyle}>{t('person2BirthDate')}</label>
                <input
                  type="date"
                  name="person2BirthDate"
                  value={formData.person2BirthDate}
                  onChange={handleInputChange}
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ec4899';
                    e.target.style.background = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.background = '#fafafa';
                  }}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>{t('focusArea')}</label>
              <input
                type="text"
                name="focusArea"
                value={formData.focusArea}
                onChange={handleInputChange}
                placeholder={t('relationshipTypePlaceholder')}
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ec4899';
                  e.target.style.background = '#ffffff';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.background = '#fafafa';
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isAnalyzing}
              style={buttonStyle}
              onMouseEnter={(e) => {
                if (!isAnalyzing) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #be185d, #ec4899)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px 0 rgba(236, 72, 153, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isAnalyzing) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #ec4899, #be185d)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(236, 72, 153, 0.3)';
                }
              }}
            >
              {isAnalyzing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  {t('analyzing')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faHeart} />
                  {t('startAnalysis')}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RelationshipNumerologyPage; 